
import { computed, defineComponent, getCurrentInstance, nextTick, onMounted, onUnmounted, onUpdated, PropType, ref, watch } from 'vue';

type SelectValue = string | number | boolean;
interface SelectOption { value: SelectValue; label: string; disabled?: boolean; layout?: string }
interface SelectRow extends SelectOption { group: string }
interface LayoutObserver { observe(element: Element): void; disconnect(): void }

export default defineComponent({
    name: 'AppSelect',
    props: {
        modelValue: { type: [String, Number, Boolean] as PropType<SelectValue>, default: undefined },
        value: { type: [String, Number, Boolean] as PropType<SelectValue>, default: undefined },
        modelModifiers: { type: Object as PropType<Record<string, boolean>>, default: () => ({}) },
        options: { type: Array as PropType<SelectOption[]>, default: () => [] },
        id: { type: String, default: undefined },
        name: { type: String, default: undefined },
        placeholder: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit, attrs }) {
        const root = ref<HTMLElement | null>(null);
        const trigger = ref<HTMLButtonElement | null>(null);
        const native = ref<HTMLSelectElement | null>(null);
        const menu = ref<HTMLElement | null>(null);
        const rows = ref<SelectRow[]>([]);
        const open = ref(false);
        const activeIndex = ref(-1);
        const invalid = ref(false);
        const validationMessage = ref('');
        const inferredLabel = ref('');
        const menuStyle = ref<Record<string, string>>({});
        const listId = 'app-select-' + getCurrentInstance()!.uid;
        const currentValue = computed(() => props.modelValue !== undefined ? props.modelValue : props.value);
        const nativeValue = computed<SelectValue | undefined>({
            get: () => currentValue.value,
            set: (value: SelectValue | undefined) => {
                const numeric = props.modelModifiers.number ? parseFloat(String(value)) : NaN;
                emit('update:modelValue', Number.isNaN(numeric) ? value : numeric);
            },
        });
        const selectedIndex = computed(() => rows.value.findIndex(option => Object.is(option.value, currentValue.value) || String(option.value) === String(currentValue.value)));
        const selected = computed(() => rows.value[selectedIndex.value]);
        const describedBy = computed(() => [attrs['aria-describedby'], invalid.value ? listId + '-error' : ''].filter(Boolean).join(' ') || undefined);
        let signature = '';
        let searchText = '';
        let searchTime = 0;
        let resizeObserver: LayoutObserver | null = null;
        let positionFrame = 0;

        // Keep native option values, grouping, form validation, and existing change handlers intact.
        function syncOptions() {
            if (!native.value) return;
            const next = Array.from(native.value.options).map(option => {
                const group = option.parentElement instanceof HTMLOptGroupElement ? option.parentElement : null;
                const value = '_value' in option ? (option as HTMLOptionElement & { _value: SelectValue })._value : option.value;
                return { value, label: option.label, group: group?.label || '', disabled: option.disabled || !!group?.disabled, ...props.options.find(item => Object.is(item.value, value)) };
            });
            const nextSignature = JSON.stringify(next);
            if (signature !== nextSignature) { signature = nextSignature; rows.value = next; schedulePosition(); }
            if (open.value && (!rows.value[activeIndex.value] || rows.value[activeIndex.value].disabled)) activeIndex.value = firstAvailable();
        }
        function firstAvailable() { return rows.value.findIndex(option => !option.disabled); }
        function positionMenu() {
            if (!open.value || !trigger.value) return;
            const rect = trigger.value.getBoundingClientRect();
            const viewport = window.visualViewport;
            const viewportLeft = viewport?.offsetLeft || 0;
            const viewportTop = viewport?.offsetTop || 0;
            const viewportRight = viewportLeft + (viewport?.width || document.documentElement.clientWidth);
            const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
            if (!trigger.value.getClientRects().length || rect.width === 0 || rect.bottom <= viewportTop || rect.top >= viewportBottom || rect.right <= viewportLeft || rect.left >= viewportRight) { close(); return; }

            // Teleported menus can escape their panel. Keep the panel's content gutters,
            // while allowing a short toolbar or field row to expand vertically.
            let left = viewportLeft + 8;
            let right = viewportRight - 8;
            let layer = 10000;
            let visibleTop = viewportTop;
            let visibleBottom = viewportBottom;
            for (let parent = root.value?.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
                const style = getComputedStyle(parent);
                // A body-level popup must sit above its owning modal's stacking context.
                layer = Math.max(layer, (parseFloat(style.zIndex) || 0) + 1);
                const paddingLeft = parseFloat(style.paddingLeft) || 0;
                const paddingRight = parseFloat(style.paddingRight) || 0;
                const bordered = parseFloat(style.borderLeftWidth) > 0 || parseFloat(style.borderRightWidth) > 0;
                const clipped = /(auto|scroll|hidden|clip)/.test(style.overflowX);
                const clippedY = /(auto|scroll|hidden|clip)/.test(style.overflowY);
                const section = /^(FORM|SECTION|ARTICLE|FIELDSET|MAIN|ASIDE)$/.test(parent.tagName) || parent.getAttribute('role') === 'dialog';
                if (!paddingLeft && !paddingRight && !bordered && !clipped && !section) continue;
                const bounds = parent.getBoundingClientRect();
                if (!parent.clientWidth) continue;
                const scale = parent.offsetWidth ? bounds.width / parent.offsetWidth : 1;
                if (clippedY) {
                    visibleTop = Math.max(visibleTop, bounds.top + parent.clientTop);
                    visibleBottom = Math.min(visibleBottom, bounds.top + parent.clientTop + parent.clientHeight);
                }
                left = Math.max(left, bounds.left + (parent.clientLeft + paddingLeft) * scale);
                right = Math.min(right, bounds.right - ((parseFloat(style.borderRightWidth) || 0) + paddingRight) * scale, bounds.left + (parent.clientLeft + parent.clientWidth - paddingRight) * scale);
                if (clipped && (rect.right <= bounds.left || rect.left >= bounds.right)) { close(); return; }
            }
            const availableWidth = right - left;
            if (availableWidth <= 0 || rect.bottom <= visibleTop || rect.top >= visibleBottom) { close(); return; }
            const width = Math.min(Math.max(rect.width, 184), availableWidth);
            const below = viewportBottom - rect.bottom - 15;
            const above = rect.top - viewportTop - 15;
            const upward = below < 200 && above > below;
            const maxHeight = Math.min(320, Math.max(0, upward ? above : below));
            menuStyle.value = {
                left: Math.max(left, Math.min(rect.left, right - width)) + 'px',
                width: width + 'px', maxHeight: maxHeight + 'px', zIndex: String(layer),
                ...(upward ? { bottom: window.innerHeight - rect.top + 7 + 'px' } : { top: rect.bottom + 7 + 'px' }),
            };
        }
        function schedulePosition() {
            if (!open.value || positionFrame) return;
            positionFrame = requestAnimationFrame(() => { positionFrame = 0; positionMenu(); });
        }
        function trackLayout() {
            window.addEventListener('scroll', onScroll, true);
            window.addEventListener('resize', schedulePosition);
            window.visualViewport?.addEventListener('resize', schedulePosition);
            window.visualViewport?.addEventListener('scroll', schedulePosition);
            const Observer = (window as Window & { ResizeObserver?: new (callback: () => void) => LayoutObserver }).ResizeObserver;
            if (Observer) {
                resizeObserver = new Observer(schedulePosition);
                for (let element: HTMLElement | null = trigger.value; element; element = element.parentElement) resizeObserver.observe(element);
            }
        }
        function stopTracking() {
            resizeObserver?.disconnect(); resizeObserver = null;
            cancelAnimationFrame(positionFrame); positionFrame = 0;
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', schedulePosition);
            window.visualViewport?.removeEventListener('resize', schedulePosition);
            window.visualViewport?.removeEventListener('scroll', schedulePosition);
        }
        function scrollActive() { nextTick(() => menu.value?.querySelector<HTMLElement>('[id="' + listId + '-' + activeIndex.value + '"]')?.scrollIntoView({ block: 'nearest' })); }
        function show() {
            if (props.disabled || !rows.value.some(option => !option.disabled)) return;
            document.dispatchEvent(new CustomEvent('app-select-open', { detail: listId }));
            activeIndex.value = selectedIndex.value >= 0 && !rows.value[selectedIndex.value].disabled ? selectedIndex.value : firstAvailable();
            open.value = true; searchText = ''; positionMenu();
            if (open.value) { stopTracking(); trackLayout(); scrollActive(); }
        }
        function close() { open.value = false; searchText = ''; stopTracking(); }
        function toggle() { if (open.value) close(); else show(); }
        function choose(index: number) {
            if (props.disabled || !native.value || !rows.value[index] || rows.value[index].disabled) return;
            if (selectedIndex.value !== index) {
                native.value.selectedIndex = index;
                native.value.dispatchEvent(new Event('change', { bubbles: true }));
            }
            close(); trigger.value?.focus();
        }
        function move(direction: number) {
            const count = rows.value.length;
            for (let step = 1; step <= count; step++) {
                const index = (activeIndex.value + direction * step + count) % count;
                if (!rows.value[index].disabled) { activeIndex.value = index; break; }
            }
            scrollActive();
        }
        function onKeyDown(event: KeyboardEvent) {
            if (props.disabled) return;
            if (event.key === 'Tab') { close(); return; }
            if (event.key === 'Escape') { if (open.value) { event.preventDefault(); event.stopPropagation(); close(); } return; }
            if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                if (!open.value) show();
                else if (event.key === 'ArrowDown') move(1);
                else if (event.key === 'ArrowUp') move(-1);
                if (event.key === 'Home') activeIndex.value = firstAvailable();
                if (event.key === 'End') activeIndex.value = rows.value.map(option => !option.disabled).lastIndexOf(true);
                scrollActive();
            } else if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (open.value) choose(activeIndex.value); else show();
            } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                event.preventDefault();
                if (!open.value) show();
                const now = Date.now();
                searchText = now - searchTime > 700 ? event.key : searchText + event.key;
                searchTime = now;
                const repeated = Array.from(searchText).every(char => char === searchText[0]);
                const query = (repeated ? event.key : searchText).toLocaleLowerCase();
                for (let step = 1; step <= rows.value.length; step++) {
                    const index = (activeIndex.value + (repeated ? step : step - 1)) % rows.value.length;
                    if (!rows.value[index].disabled && rows.value[index].label.toLocaleLowerCase().startsWith(query)) { activeIndex.value = index; scrollActive(); break; }
                }
            }
        }
        function onChange(event: Event) { invalid.value = false; emit('change', event); }
        function onInvalid() { invalid.value = true; validationMessage.value = native.value?.validationMessage || ''; trigger.value?.focus(); }
        function onPointerDown(event: PointerEvent) { const target = event.target as Node; if (!root.value?.contains(target) && !menu.value?.contains(target)) close(); }
        function onFocusOut(event: FocusEvent) { const target = event.relatedTarget as Node | null; if (!root.value?.contains(target) && !menu.value?.contains(target)) close(); }
        function onOtherOpen(event: Event) { if ((event as CustomEvent).detail !== listId) close(); }
        function onScroll(event: Event) { if (!(event.target instanceof Node) || !menu.value?.contains(event.target)) schedulePosition(); }
        watch(() => props.disabled, disabled => { if (disabled) close(); });
        watch(currentValue, () => { invalid.value = false; });
        onMounted(() => {
            syncOptions();
            const label = root.value?.closest('label') || (props.id ? Array.from(document.querySelectorAll('label')).find(label => label.htmlFor === props.id) : null);
            if (label) inferredLabel.value = Array.from(label.childNodes).filter(node => node !== root.value).map(node => node.textContent).join(' ').trim();
            document.addEventListener('pointerdown', onPointerDown);
            document.addEventListener('app-select-open', onOtherOpen);
        });
        onUpdated(syncOptions);
        onUnmounted(() => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('app-select-open', onOtherOpen);
            stopTracking();
        });
        return { attrs, root, trigger, native, menu, rows, open, activeIndex, invalid, validationMessage, inferredLabel, listId, nativeValue, selectedIndex, selected, describedBy, menuStyle, toggle, choose, onKeyDown, onChange, onInvalid, onFocusOut };
    },
});
